import React from "react";

/**
 * ============================================================
 * Carte accordéon premium
 * ============================================================
 *
 * Ce composant gère :
 * - le titre
 * - le résumé
 * - l’action ouvrir / fermer
 * - les badges optionnels
 * - le contenu déplié
 *
 * Il ne contient aucune logique métier.
 */
const AccountAccordionCard = ({
  title,
  preview,
  secondaryPreview = "",
  isOpen,
  onToggle,
  badges = [],
  actionLabelOpen = "Masquer",
  actionLabelClosed = "Afficher",
  children,
}) => {
  /**
   * ============================================================
   * Libellé d’action
   * ============================================================
   *
   * Ce bloc choisit le texte affiché à droite selon l’état.
   */
  const actionLabel = isOpen ? actionLabelOpen : actionLabelClosed;

  return (
    <section
      className={`luxury-card-section luxury-accordion-card ${
        isOpen ? "is-open" : ""
      }`}
    >
      <style>{`
        .luxury-accordion-card {
          border: 1px solid rgba(191, 167, 106, 0.16);
          border-radius: 28px;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(252,249,242,0.98) 100%);
          box-shadow:
            0 12px 30px rgba(0,0,0,0.05),
            inset 0 1px 0 rgba(255,255,255,0.65);
          overflow: hidden;
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }

        .luxury-accordion-card.is-open {
          box-shadow:
            0 18px 42px rgba(0,0,0,0.08),
            inset 0 1px 0 rgba(255,255,255,0.75);
        }

        .luxury-accordion-trigger {
          width: 100%;
          border: none;
          background: transparent;
          padding: 0;
          cursor: pointer;
          text-align: left;
        }

        .luxury-accordion-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          padding: 24px 26px;
        }

        .luxury-accordion-main {
          min-width: 0;
          flex: 1 1 auto;
        }

        .luxury-accordion-title {
          margin: 0;
          font-size: 1.1rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          font-family: "Cormorant Garamond", serif;
          color: #1d1d1d;
        }

        .luxury-accordion-preview {
          margin: 10px 0 0;
          color: rgba(0,0,0,0.72);
          font-size: 0.98rem;
          line-height: 1.5;
        }

        .luxury-accordion-secondary-preview {
          margin: 8px 0 0;
          color: rgba(0,0,0,0.56);
          font-size: 0.9rem;
          line-height: 1.45;
        }

        .luxury-accordion-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
          flex-shrink: 0;
        }

        .luxury-accordion-link {
          font-size: 0.9rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.62);
          white-space: nowrap;
        }

        .luxury-accordion-chevron {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          border: 1px solid rgba(191, 167, 106, 0.28);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(191, 167, 106, 0.08);
          color: #8f7442;
          font-size: 1.15rem;
          line-height: 1;
          flex-shrink: 0;
        }

        .luxury-accordion-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .luxury-accordion-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 30px;
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 0.8rem;
          letter-spacing: 0.04em;
          border: 1px solid rgba(191, 167, 106, 0.24);
          background: rgba(191, 167, 106, 0.08);
          color: #7a6338;
          white-space: nowrap;
        }

        .luxury-accordion-badge.is-strong {
          background: rgba(39,174,96,0.08);
          border-color: rgba(39,174,96,0.2);
          color: #1f6f43;
        }

        .luxury-accordion-content {
          border-top: 1px solid rgba(191, 167, 106, 0.14);
          padding: 22px 26px 26px;
        }

        @media (max-width: 780px) {
          .luxury-accordion-head {
            padding: 20px 18px;
            gap: 14px;
          }

          .luxury-accordion-title {
            font-size: 0.98rem;
            line-height: 1.1;
          }

          .luxury-accordion-preview {
            font-size: 0.92rem;
            margin-top: 8px;
          }

          .luxury-accordion-secondary-preview {
            font-size: 0.84rem;
            margin-top: 6px;
          }

          .luxury-accordion-right {
            flex-direction: row;
            align-items: center;
            gap: 10px;
          }

          .luxury-accordion-link {
            font-size: 0.8rem;
          }

          .luxury-accordion-chevron {
            width: 34px;
            height: 34px;
          }

          .luxury-accordion-badges {
            display: none;
          }

          .luxury-accordion-content {
            padding: 18px 18px 20px;
          }
        }
      `}</style>

      <button
        type="button"
        className="luxury-accordion-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="luxury-accordion-head">
          <div className="luxury-accordion-main">
            <h3 className="luxury-accordion-title">{title}</h3>

            {preview ? (
              <p className="luxury-accordion-preview">{preview}</p>
            ) : null}

            {secondaryPreview ? (
              <p className="luxury-accordion-secondary-preview">
                {secondaryPreview}
              </p>
            ) : null}
          </div>

          <div className="luxury-accordion-right">
            {Array.isArray(badges) && badges.length > 0 ? (
              <div className="luxury-accordion-badges">
                {badges.map((badge, index) => (
                  <span
                    key={`${badge?.label || "badge"}-${index}`}
                    className={`luxury-accordion-badge ${
                      badge?.strong ? "is-strong" : ""
                    }`}
                  >
                    {badge?.label || ""}
                  </span>
                ))}
              </div>
            ) : null}

            <span className="luxury-accordion-link">{actionLabel}</span>
            <span className="luxury-accordion-chevron">
              {isOpen ? "−" : "+"}
            </span>
          </div>
        </div>
      </button>

      {isOpen ? (
        <div className="luxury-accordion-content">{children}</div>
      ) : null}
    </section>
  );
};

export default AccountAccordionCard;
